import { memo } from 'react';
import { Progress } from 'radix-ui';
import { useGameDispatch } from '../../store/global-context';
import { useGeneratorsStore } from '../../store/zustand/useGeneratorsStore';
import { useCurrencyStore } from '../../store/zustand/useCurrencyStore';
import styles from './styles.module.scss';
import { GUIActions } from '../../types/game-types';
import { SmallButton } from '../components/SmallButton';
import { Icon } from '../Icon';
import { Debug, humanNumber, HotkeyHint } from '@game-libs/exc-libs';
import { useHotkeys } from 'react-hotkeys-hook';
import { HelperTooltip } from '../components/HelperTooltip';
import { InspirationMeterTooltipContent } from '../components/TooltipContents/InspirationMeterTooltipContent';
import tooltipStyles from '../components/TooltipContents/styles.module.scss';
import { useUpgradesStore } from '../../store/zustand/useUpgradesStore';

export const InspirationMeter = memo(function InspirationMeter() {
  const inspiration = useCurrencyStore((state) => state.inspiration);
  const upgrades = useUpgradesStore((store) => store.upgrades);
  const generators = useGeneratorsStore((store) => store.generators);
  const dispatch = useGameDispatch();

  const maxInspirationAmount = 100; // inspiration points
  const maxActiveAmount = upgrades.bard.obnoxiousGuitarist.multiplier; // seconds active

  // Get max multiplier from bard upgrades
  const maxInspirationMultiplier = upgrades.bard.luteSolo.multiplier;

  const hasReachedMaxMultiplier =
    inspiration.multiplier >= maxInspirationMultiplier && inspiration.remainingActive === 0;



  let progress = { max: maxInspirationAmount, current: inspiration.amount };

  if (inspiration.remainingActive > 0) {
    progress = { max: maxActiveAmount, current: inspiration.remainingActive };
  }

  const isCoolingDown = inspiration.remainingCooldown > 0;

  const canActivate = inspiration.remainingActive === 0 && inspiration.multiplier > 1;

  useHotkeys(
    't',
    () => {
      if (canActivate) dispatch({ type: GUIActions.ACTIVATE_INSPIRATION });
    },
    [canActivate, dispatch],
  );

  if (inspiration.amount === 0 && generators.bard.owned === 0) {
    return null;
  }

  return (
    <div className={`${styles.inspirationMeter} ${isCoolingDown && styles.isCoolingDown}`}>
      <div className={styles.harpButton}>
        <SmallButton shadow disabled={!canActivate} onClick={() => dispatch({ type: GUIActions.ACTIVATE_INSPIRATION })}>
          <Icon iconId={'harp'} large hasBackground={false} />
          <HotkeyHint hint="t" />
        </SmallButton>
      </div>

      <HelperTooltip content={<InspirationMeterTooltipContent />} contentClassName={tooltipStyles.tooltipContent}>
        <Progress.Root
          className={`${styles.inspirationBar} ${inspiration.remainingActive > 0 && styles.active}`}
          id="inspiration"
          value={progress.current}
          max={progress.max}
        >
          <Progress.Indicator
            className={styles.inspirationBarValue}
            style={{ width: `${(100 * progress.current) / progress.max}%` }}
          />
        </Progress.Root>
      </HelperTooltip>

      <div className={`${styles.inspirationMultiplier} ${hasReachedMaxMultiplier && styles.reachedMax}`}>
        x{inspiration.multiplier}
      </div>
      {inspiration.remainingCooldown > 0 && <Debug alwaysShow absolute>{inspiration.remainingCooldown}</Debug>}
      {inspiration.remainingActive > 0 && <Debug alwaysShow absolute>{inspiration.remainingActive}</Debug>}
      {inspiration.amount > 0 && <Debug alwaysShow absolute>{humanNumber(inspiration.amount, 3)}/{humanNumber(maxInspirationAmount, 0)}</Debug>}
    </div>
  );
});
